/* ============================================================================
 * TOTH WISHLIST · v1 (2026-05-12)
 * ============================================================================
 * Guest-first localStorage wishlist · zero captura passiva de email.
 *
 * Princípios (regras do dono · 2026-05-12):
 *   - localStorage como fonte de verdade. Funciona sem login.
 *   - ZERO captura passiva de email. Cliente só compartilha email se explicitamente
 *     clicar em "Compartilhar por email" ou "Avise-me sobre este produto".
 *   - Progressive enhancement: View Transitions API quando disponível, fallback
 *     gracioso em browsers antigos (Safari < 18.2, Firefox sem flag, etc.).
 *   - Respeita prefers-reduced-motion.
 *   - Integra com lead-tracker via dataLayer push (event: AddToWishlist).
 *
 * API pública (window.TothWishlist):
 *   .add(handle, id)      → adiciona produto
 *   .remove(handle)       → remove produto
 *   .toggle(handle, id)   → adiciona se ausente, remove se presente
 *   .has(handle)          → boolean
 *   .list()               → array de items
 *   .count()              → number
 *   .clear()              → limpa tudo
 *   .subscribe(fn)        → observer pattern (retorna unsubscribe)
 *
 * Eventos custom no document:
 *   'toth-wishlist:change' (detail: { items, count })
 *   'toth-wishlist:add'    (detail: { item })
 *   'toth-wishlist:remove' (detail: { handle })
 * ============================================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'toth_wishlist_v1';
  var SCHEMA_VERSION = 1;
  var MAX_ITEMS = 100; // limite razoável

  var subscribers = [];

  // ───── Storage helpers (fail-silent) ─────
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { version: SCHEMA_VERSION, items: [] };
      var data = JSON.parse(raw);
      if (!data || data.version !== SCHEMA_VERSION || !Array.isArray(data.items)) {
        return { version: SCHEMA_VERSION, items: [] };
      }
      return data;
    } catch (e) {
      return { version: SCHEMA_VERSION, items: [] };
    }
  }

  function write(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (e) {
      return false;
    }
  }

  function notify(type, detail) {
    try {
      document.dispatchEvent(new CustomEvent('toth-wishlist:' + type, { detail: detail }));
    } catch (e) {}
    subscribers.forEach(function (fn) {
      try { fn(detail); } catch (e) {}
    });
  }

  // ───── Public API ─────
  var TothWishlist = {
    add: function (handle, id) {
      if (!handle) return false;
      var data = read();
      if (data.items.some(function (i) { return i.handle === handle; })) return false;
      if (data.items.length >= MAX_ITEMS) data.items.shift();
      var item = { handle: handle, id: id || null, added_at: Date.now() };
      data.items.push(item);
      write(data);
      notify('add', { item: item });
      notify('change', { items: data.items, count: data.items.length });
      pushAnalytics('AddToWishlist', item);
      return true;
    },

    remove: function (handle) {
      if (!handle) return false;
      var data = read();
      var before = data.items.length;
      data.items = data.items.filter(function (i) { return i.handle !== handle; });
      if (data.items.length === before) return false;
      write(data);
      notify('remove', { handle: handle });
      notify('change', { items: data.items, count: data.items.length });
      pushAnalytics('RemoveFromWishlist', { handle: handle });
      return true;
    },

    toggle: function (handle, id) {
      if (!handle) return false;
      return this.has(handle) ? this.remove(handle) : this.add(handle, id);
    },

    has: function (handle) {
      if (!handle) return false;
      var data = read();
      return data.items.some(function (i) { return i.handle === handle; });
    },

    list: function () {
      return read().items.slice(); // cópia defensiva
    },

    count: function () {
      return read().items.length;
    },

    clear: function () {
      write({ version: SCHEMA_VERSION, items: [] });
      notify('change', { items: [], count: 0 });
    },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      subscribers.push(fn);
      return function () {
        var idx = subscribers.indexOf(fn);
        if (idx !== -1) subscribers.splice(idx, 1);
      };
    }
  };

  // ───── Analytics (lead-tracker via dataLayer) ─────
  function pushAnalytics(eventName, detail) {
    if (!window.dataLayer) window.dataLayer = [];
    try {
      window.dataLayer.push({
        event: eventName,
        ecommerce: {
          items: [{
            item_id: detail.id || null,
            item_handle: detail.handle,
            item_name: detail.title || null
          }]
        },
        wishlist_count: read().items.length
      });
    } catch (e) {}
  }

  // ───── Heart buttons (delegation) ─────
  function syncHeartUI(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    var saved = TothWishlist.has(handle);
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var label = button.getAttribute('data-label-' + (saved ? 'saved' : 'idle'));
    if (label) button.setAttribute('aria-label', label);
  }

  function bindHearts(root) {
    root = root || document;
    var buttons = root.querySelectorAll('[data-toth-wishlist-heart]');
    Array.prototype.forEach.call(buttons, syncHeartUI);
  }

  // NOTA: o toggle é disparado via onclick INLINE no <button> heart
  // (definido em snippets/toth-wishlist-heart.liquid). Aqui NÃO usamos
  // event delegation pra evitar disparo duplicado (que estaria
  // resultando em add+remove em sequência = zero efeito no localStorage).

  // ───── Header counter ─────
  function syncHeaderCounter() {
    var n = TothWishlist.count();
    var bubble = document.querySelector('[data-toth-wishlist-count]');
    if (!bubble) return;
    var span = bubble.querySelector('span[aria-hidden]') || bubble;
    span.textContent = n < 100 ? String(n) : '99+';
    if (n > 0) {
      bubble.removeAttribute('hidden');
    } else {
      bubble.setAttribute('hidden', '');
    }
  }

  // ───── Page /favoritos: render saved products ─────
  function renderWishlistPage() {
    var pageRoot = document.querySelector('[data-toth-wishlist-page]');
    if (!pageRoot) return;

    var grid = pageRoot.querySelector('[data-toth-wishlist-grid]');
    var emptyState = pageRoot.querySelector('[data-toth-wishlist-empty]');
    var loadingState = pageRoot.querySelector('[data-toth-wishlist-loading]');

    var items = TothWishlist.list();

    if (loadingState) loadingState.setAttribute('hidden', '');

    if (!items.length) {
      if (emptyState) emptyState.removeAttribute('hidden');
      if (grid) grid.setAttribute('hidden', '');
      return;
    }

    if (emptyState) emptyState.setAttribute('hidden', '');
    if (grid) {
      grid.removeAttribute('hidden');
      grid.innerHTML = '';
    }

    // Fetch + render cada produto
    items.forEach(function (item, idx) {
      fetch('/products/' + encodeURIComponent(item.handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('not_found');
          return r.json();
        })
        .then(function (product) {
          if (!grid) return;
          var card = renderCard(product);
          grid.appendChild(card);
        })
        .catch(function () {
          // produto não existe mais → remove silenciosamente
          TothWishlist.remove(item.handle);
        });
    });
  }

  function renderCard(product) {
    var article = document.createElement('article');
    article.className = 'toth-wishlist-card';
    article.setAttribute('data-handle', product.handle);

    var price = product.price_min || product.price || 0;
    var priceFormatted = (price / 100).toLocaleString('pt-BR', {
      style: 'currency', currency: 'BRL', minimumFractionDigits: 2
    });

    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;

    article.innerHTML =
      '<a class="toth-wishlist-card__link" href="/products/' + escapeAttr(product.handle) + '" aria-label="' + escapeAttr(product.title) + '">' +
        '<div class="toth-wishlist-card__media">' +
          (img ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(product.title) + ' | Toth Móveis" loading="lazy" decoding="async">' : '') +
        '</div>' +
        '<div class="toth-wishlist-card__info">' +
          '<h3 class="toth-wishlist-card__title">' + escapeHtml(product.title) + '</h3>' +
          (product.metafields && product.metafields.custom && product.metafields.custom.hide_price === true ? '' :
            '<p class="toth-wishlist-card__price">' + priceFormatted + '</p>') +
        '</div>' +
      '</a>' +
      '<button type="button" class="toth-wishlist-card__remove" data-toth-wishlist-remove="' + escapeAttr(product.handle) + '" aria-label="Remover dos favoritos">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>' +
        '</svg>' +
      '</button>';

    article.querySelector('[data-toth-wishlist-remove]').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var doRemove = function () {
        TothWishlist.remove(product.handle);
        article.remove();
        // Re-renderiza se ficou vazio
        if (!TothWishlist.count()) renderWishlistPage();
      };
      if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        try { document.startViewTransition(doRemove); } catch (e) { doRemove(); }
      } else {
        doRemove();
      }
    });

    return article;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ───── Boot ─────
  function boot() {
    bindHearts();
    syncHeaderCounter();
    renderWishlistPage();

    // Re-sync ao mudar
    document.addEventListener('toth-wishlist:change', function () {
      bindHearts();
      syncHeaderCounter();
    });

    // Re-sync ao voltar via back/forward cache
    window.addEventListener('pageshow', function () {
      bindHearts();
      syncHeaderCounter();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose global
  window.TothWishlist = TothWishlist;
})();
